import { useCallback, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ResourceKitProvider } from '@resource-kit/core/react';
import {
  DENY_ALL,
  type Confirm,
  type I18nProbe,
  type Notify,
  type PreferenceStore,
  type ResourceKitServices,
  type Translate,
} from '@resource-kit/core';
import { useToast } from '@components/ui/Toast';
import { useConfirm } from '@components/ui/ConfirmDialog';
import { useOptionalAuthUserId, useOptionalIsAdmin, usePermissions } from '@hooks/useAuth';
import { createStore } from '@resource-kit/controllers';
import { FormViewSlotsProvider } from '@resource-kit/ui/react';
import { hgsFormViewSlots } from './resourceKitSlots';
import i18nInstance from '@/i18n';
import { HOST_FORMATTERS } from './hostFormatters';

/**
 * The hgs adapter for the resource kit's services seam (#2165, ADR-0035 D3).
 *
 * The kit declares WHAT it needs (`@resource-kit/core`'s `services`); this file
 * is the only place that says HOW this app provides it. Everything host-shaped
 * — i18next, the toast provider, the confirm dialog's vocabulary, the auth
 * context, the `hgs.` storage namespace — is named HERE and nowhere inside the
 * kit, which is the whole point of the seam.
 *
 * Read it as six small translations, each stated once:
 *
 *   t        react-i18next's `t`, narrowed to the seam's signature
 *   i18n     the two non-translation i18n facts (active locale, key exists)
 *   format   the host's six formatters, passed through by name
 *   notify   one `(kind, …)` call routed onto the host's four toast methods
 *   confirm  the kit's `intent` mapped onto the host dialog's `type`
 *   storage  a logical key namespaced, versioned and scoped to the caller
 *
 * ── THE OBJECT'S IDENTITY IS A TREE-WIDE INVALIDATION SIGNAL ──
 *
 * What this function returns becomes the context value that ~22 kit consumers
 * read, so rebuilding it re-renders every table, form and detail panel at once.
 * That is exactly right for a language switch or a change of caller, and
 * exactly wrong for a toast. Both host providers publish callables that keep
 * one identity for the life of their mount (#2171): `Toast.tsx`'s six are
 * `useCallback`s taking `[]` or `[addToast]`, and `ConfirmDialog.tsx`'s
 * `confirm` takes `[]` because it only calls `setDialog` — with the one
 * exception of `ConfirmDialog.tsx`'s `alert`, which takes `[t]` and so is
 * rebuilt on a language switch; this seam destructures only `confirm` and
 * never reads it. So this file
 * destructures them and depends on them directly — there is nothing left for
 * this seam to compensate for on the consumer side.
 *
 * So the members split three ways, and the split is the design, not a
 * micro-optimisation:
 *
 *   FROZEN     `format` is host module functions — a module-level constant.
 *   BORROWED   `notify` / `confirm` name the host callables they wrap in their
 *              own dependency arrays (`[success, error, warning, info]`,
 *              `[hostConfirm]`), so they hold one identity for exactly as long
 *              as the host's do — which, for those five, is the whole mount.
 *   REACTIVE   `t`, `i18n`, `access` and `storage` MUST change identity when
 *              what they answer changes — that propagation is the only thing
 *              that repaints translated copy, re-gates affordances after a
 *              sign-in, and re-scopes preferences to a different user. Freezing
 *              them would be a correctness bug wearing a performance costume.
 */

/** The seam's storage namespace inside `createStore`'s key space. */
const HOST_STORAGE_PREFIX = 'hgs.';

/**
 * Envelope version for everything written through the seam. `1` is not a fresh
 * choice — it is the version `useDataTableParams` has always written under, and
 * changing it here would orphan every filter set operators have already saved.
 */
const HOST_STORAGE_VERSION = 1;

/**
 * No `sanitize`. The sanitizer `createStore` takes — `StoreConfig.sanitize` in
 * `packages/resource-kit-controllers/util/store.ts` — is a value-SHAPE check,
 * and the seam deliberately does not know the shape. A sanitizer narrow enough to be useful
 * would read a shape it was not written for as corrupt and DELETE it.
 * Validation stays with the consumer, which is the only party that knows what
 * it wrote — `useDataTableParams` does exactly that, discarding a non-string
 * entry itself rather than asking the store to judge it.
 *
 * The second shape — the column-visibility envelope — arrived on this seam in
 * #2169 PR-2. It used to call `createStore` directly, with its own `sanitize`;
 * now `@resource-kit/ui`'s `useColumnVisibility` reads and writes the logical
 * key `columns.<base>` through `storage` below, and validates the blob itself,
 * removing a present-but-wrong-shaped one. (`components/ui/useColumnVisibility.ts`
 * still exists, but only as a re-export shim of the package until #2170.)
 * Omitting `sanitize` here is what made that a re-point rather than a data
 * migration: the physical key and version are unchanged, so every blob written
 * before the move still reads back.
 */
const hostStore = (key: string) =>
  createStore<unknown>({
    key: `${HOST_STORAGE_PREFIX}${key}`,
    version: HOST_STORAGE_VERSION,
    scope: 'user',
  });

export function HgsResourceKitProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { success, error, warning, info } = useToast();
  const { confirm: hostConfirm } = useConfirm();
  const permissions = usePermissions();
  const isAdmin = useOptionalIsAdmin();
  const userId = useOptionalAuthUserId();

  /**
   * Wrapped, not passed through: `TFunction` is an overloaded generic whose
   * options type is derived from the key, and the seam's `t` is a plain
   * two-argument function. The wrapper's identity still tracks `t`, which is
   * what makes a language switch re-render every kit surface.
   */
  const translate = useCallback<Translate>((key, opts) => t(key, opts), [t]);

  // The host's four toast kinds are mount-stable (`Toast.tsx`), so this closure
  // is rebuilt only if that ever stops being true — no ref indirection needed to
  // keep one `notify` identity for the lifetime of the mount.
  const notify = useCallback<Notify>(
    (kind, title, message) => {
      ({ success, error, warning, info })[kind](title, message);
    },
    [success, error, warning, info],
  );

  // `intent` in, `type` out. The kit says how serious the question is; the
  // host dialog's own word for that idea stays inside the host.
  const confirm = useCallback<Confirm>(
    ({ intent, ...request }) => hostConfirm({ ...request, type: intent ?? 'default' }),
    [hostConfirm],
  );

  // The physical key stays `<userId>:hgs.<logical key>` at whatever version
  // the consumer already used — the seam changes who CALLS the store, not
  // what lands in localStorage. Scoped to `userId` and therefore NOT hoistable:
  // a frozen store would keep writing the previous operator's key after a
  // sign-in, which is a data bug, not a lost render.
  const storage = useMemo<PreferenceStore>(
    () => ({
      read: (key) => hostStore(key).read(userId),
      write: (key, value) => hostStore(key).write(value, userId),
      remove: (key) => hostStore(key).remove(userId),
      has: (key) => hostStore(key).has(userId),
    }),
    [userId],
  );

  const services = useMemo<ResourceKitServices>(() => {
    /**
     * Read off the module singleton rather than `useTranslation().i18n`: a
     * handful of suites mock `react-i18next` with a factory that returns only
     * `t`, and reaching for `.i18n` there would crash a page that has nothing
     * to do with locale probing. `exists` is wrapped in an arrow because it is
     * an unbound method — handing over the reference alone loses `this`.
     */
    const i18n: I18nProbe = {
      language: i18nInstance.language,
      exists: (key) => i18nInstance.exists(key),
    };

    return {
      // No real implementation yet: the kit's `canAccess` consumers land with
      // #1799, and inventing a decision function before there is a caller
      // would be guessing at a permission model. DENY until then — the same
      // answer the unmounted provider gave.
      canAccess: DENY_ALL,
      t: translate,
      i18n,
      format: HOST_FORMATTERS,
      notify,
      confirm,
      storage,
      access: { isAdmin, hasGrant: (permission) => permissions.includes(permission) },
    };
  }, [translate, notify, confirm, storage, permissions, isAdmin]);

  return (
    <ResourceKitProvider services={services}>
      {/* The slot WIRING is not here: `FormViewSlotsProvider` only carries it,
          and the hgs implementations live next door in `./resourceKitSlots`. */}
      <FormViewSlotsProvider {...hgsFormViewSlots}>{children}</FormViewSlotsProvider>
    </ResourceKitProvider>
  );
}
